/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * The banner that tells someone their gym access changed.
 */

import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconInfoCircle, IconShieldCheck } from "@tabler/icons-react";
import { motion, useReducedMotion } from "motion/react";
import { readDismissedNotices, useAppSelector } from "@lemonadetraining/web-shared";
import { Alert, AlertDescription, AlertTitle, Button } from "@lemonadetraining/web-ui";
import { useUserAccess } from "../../hooks/useUserAccess";
import { useAcknowledgeNotice } from "../../hooks/useAcknowledgeNotice";
import { presentNotice, surfaceOf } from "./accessNoticeCopy";
import type { StoredNotice } from "./accessNoticeCopy";

const RoleChangeBanner: React.FC = () => {
  const navigate = useNavigate();
  const uid = useAppSelector((state) => state.user.user?.uid ?? null);
  const unread: StoredNotice[] = useAppSelector((state) => state.accessNotices.unread);
  const { isBoxCoach, isBoxAdmin, isLoading, boxID } = useUserAccess();
  const acknowledge = useAcknowledgeNotice();
  const reduceMotion = useReducedMotion();

  const [locallyDismissed, setLocallyDismissed] = useState<string[]>(() =>
    readDismissedNotices(uid)
  );

  const current = useMemo(() => {
    // A demotion's reassurance ("you're still a coach here") is drawn from the
    // claims, so it waits for the refresh carrying the demotion; shown early it
    // states the pre-demotion truth and then silently changes. The wait skips
    // that ONE notice rather than everything queued behind it, and applies only
    // to the gym whose claims we are reading.
    const waitingOnDemotion = (entry: StoredNotice) =>
      entry.kind === "demoted_admin" &&
      // While the claims are resolving, `boxID` is one of the things that has
      // not resolved, so it cannot narrow anything: hold regardless of gym.
      // Once resolved, this gym's admin bit only speaks for this gym.
      (isLoading || (isBoxAdmin && entry.boxID === boxID));

    const candidate = unread.find(
      (notice) =>
        surfaceOf(notice.kind) === "banner" &&
        !locallyDismissed.includes(notice.id) &&
        !waitingOnDemotion(notice)
    );
    if (!candidate) return null;
    const presented = presentNotice(candidate, { isBoxCoach });
    return presented ? { notice: candidate, presented } : null;
  }, [unread, locallyDismissed, isBoxCoach, isBoxAdmin, isLoading, boxID]);

  const dismiss = useCallback(async () => {
    if (!current) return;
    const { id } = current.notice;
    // Hidden here first: the banner must not sit there through the round trip.
    setLocallyDismissed((previous) => [...previous, id]);
    await acknowledge(id);
  }, [current, acknowledge]);

  if (!current) return null;

  const { presented, notice } = current;
  const Icon = notice.kind === "promoted_admin" ? IconShieldCheck : IconInfoCircle;
  // Only offer the Business Portal once the claims that open it have landed;
  // a button that denies is worse than no button.
  const showAction = Boolean(presented.action && (notice.kind !== "promoted_admin" || isBoxAdmin));

  // DESIGN-SPEC 5.7: fades in over 150 ms, and not at all under reduced motion.
  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: reduceMotion ? 0 : 0.15 }}
    >
      <Alert
        role="status"
        className="mb-4"
        data-testid="role-change-banner"
        data-notice-id={notice.id}
      >
        <Icon aria-hidden="true" />
        <AlertTitle>{presented.title}</AlertTitle>
        <AlertDescription className="flex flex-col items-start gap-2">
          {presented.body.map((line) => (
            <span key={line}>{line}</span>
          ))}
          <div className="flex flex-wrap items-center gap-2">
            {showAction && presented.action && (
              <Button
                size="sm"
                onClick={() => {
                  void dismiss();
                  const { to, state } = presented.action!;
                  navigate(to, { state });
                }}
                data-testid="role-change-banner-action"
              >
                {presented.action.label}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void dismiss()}
              aria-label="Dismiss"
              data-testid="role-change-banner-dismiss"
            >
              Dismiss
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </motion.div>
  );
};

export default RoleChangeBanner;
